import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Dimensions, Metrics } from '@/api/types';
import { metricValue, resolveMetric, sourceLabel } from '@/api/metric-resolution';
import { Callout } from '@/components/Callout';
import { ExportButton } from '@/components/ExportButton';
import { FilterBar, type ActiveFilter } from '@/components/FilterBar';
import { Select, Segmented } from '@/components/Select';
import { Funnel, type FunnelStage } from '@/components/Funnel';
import { Heatmap, dayLabel, hourLabel } from '@/components/Heatmap';
import { Paginator } from '@/components/Paginator';
import { RadialClock } from '@/components/RadialClock';
import { RangeBar } from '@/components/RangeBar';
import { formatCompact } from '@/components/TrendChart';
import { useOverview } from '@/hooks/useOverview';
import { AnalyticsLoadError, noSourceSentence } from '@/components/AnalyticsLoadError';

const HEATMAP_METRICS = [
  { key: Metrics.sessions, label: 'Sessions' },
  { key: Metrics.activeUsers, label: 'Users' },
  { key: Metrics.leads, label: 'Leads' },
] as const;

/**
 * The analyses that need shapes the other tabs cannot provide: a 2-D grid, an ordered funnel,
 * and a paginated + cross-filtered table.
 */
export function InsightsPage({ siteKey }: { siteKey: string }) {
  const { data, isError: overviewFailed, error: overviewError, days, setDays, from, to, setRange, isCustomRange } =
    useOverview(siteKey);

  const [filter, setFilter] = useState<ActiveFilter | null>(null);
  const [metric, setMetric] = useState<string>(Metrics.sessions);
  const [dimension, setDimension] = useState<string>(Dimensions.pagePath);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const heatmap = useQuery({
    queryKey: ['matrix', siteKey, from, to, metric],
    queryFn: () => api.matrix(siteKey, Dimensions.dayOfWeek, Dimensions.hourOfDay, metric, from, to),
    retry: false,
  });

  const rows = useQuery({
    queryKey: ['breakdown', siteKey, dimension, page, perPage, filter?.dimension, filter?.value, from, to],
    queryFn: () =>
      api.breakdown(siteKey, dimension, {
        page,
        perPage,
        from,
        to,
        ...(filter?.value ? { filterDimension: filter.dimension, filterValue: filter.value } : {}),
      }),
    retry: false,
    // Hold the last page while the next loads rather than flashing an empty table.
    placeholderData: (previous) => previous,
  });

  const funnelStages = useMemo<FunnelStage[]>(() => {
    const totals = data?.totals;
    if (!totals) return [];

    // The funnel starts at sessions: none measured means no funnel, never one that starts at 0. The label names
    // whoever answered — Clarity stands in when GA4 has no figure, and its sessions mean something slightly different.
    const resolved = resolveMetric(totals, 'sessions');
    if (!resolved) return [];
    const sessions = resolved.value;
    const engaged = totals[Metrics.engagedSessions];
    const scrollDepth = totals[Metrics.averageScrollDepth];
    const leads = totals[Metrics.leads];

    const stages: FunnelStage[] = [
      { label: 'Sessions', value: sessions, source: sourceLabel(resolved) ?? 'unknown' },
    ];

    if (engaged !== undefined) {
      stages.push({ label: 'Engaged sessions', value: engaged, source: 'GA4' });
    }

    if (scrollDepth !== undefined && sessions > 0) {
      stages.push({
        label: 'Scrolled past halfway',
        // An ESTIMATE, and labelled as one: Clarity reports an average scroll depth, not a count
        // of sessions that passed 50%. Presenting a derived figure as measured would be a lie
        // that no one could catch from the chart.
        value: Math.round(sessions * Math.min(1, scrollDepth / 100)),
        source: 'Clarity',
        note: 'estimated from average scroll depth',
      });
    }

    if (leads !== undefined) {
      stages.push({ label: 'Leads', value: leads, source: 'First-party' });
    }

    return stages;
  }, [data]);

  const cells = heatmap.data?.cells ?? [];
  const hasCrossTabs = cells.length > 0;

  // Collapses the day axis out of the grid. Free — the cells are already loaded, so the dial costs
  // no extra request and can never disagree with the heatmap above it.
  const hourly = useMemo(() => {
    const totals = new Array<number>(24).fill(0);
    let seen = false;
    for (const cell of cells) {
      const hour = Number.parseInt(cell.col, 10);
      if (Number.isInteger(hour) && hour >= 0 && hour < 24) {
        totals[hour] = (totals[hour] ?? 0) + cell.value;
        seen = true;
      }
    }
    return seen ? totals.map((value, hour) => ({ hour, value })) : [];
  }, [cells]);

  return (
    <>
      <RangeBar
        days={days} setDays={setDays} from={from} to={to} data={data}
        setRange={setRange} isCustomRange={isCustomRange}
        actions={<ExportButton siteKey={siteKey} from={from} to={to} label="Export all (Excel)" />}
      />

      {/* ONE filter row, above everything it scopes. */}
      <FilterBar
        siteKey={siteKey}
        active={filter}
        onChange={(f) => { setFilter(f); setPage(1); }}
      />

      {overviewFailed && (
        <AnalyticsLoadError error={overviewError} what="the funnel totals" notFound={noSourceSentence(siteKey)} />
      )}
      {heatmap.isError && <AnalyticsLoadError error={heatmap.error} what="the day × hour grid" />}

      {!hasCrossTabs && !heatmap.isLoading && !heatmap.isError && (
        <Callout tone="info" title="Cross-tab data not collected yet">
          The heatmap and filters need two-dimension rows (day × hour, country × page). GA4 starts
          collecting them on the next ingestion after this deploy — and there is no traffic to
          collect until the landing page itself is live.
        </Callout>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>When people visit</h2>
          <Segmented
            label="Metric"
            value={metric}
            onChange={setMetric}
            options={HEATMAP_METRICS.map((m) => ({ value: m.key, label: m.label }))}
            inline
          />
        </div>
        <div className={`card-body${heatmap.isFetching ? ' is-refetching' : ''}`}>
          <Heatmap
            rowKeys={heatmap.data?.row_keys ?? []}
            colKeys={heatmap.data?.col_keys ?? []}
            cells={cells}
            rowLabel={dayLabel}
            colLabel={hourLabel}
            metricLabel={HEATMAP_METRICS.find((m) => m.key === metric)?.label.toLowerCase() ?? 'sessions'}
          />
        </div>
      </div>

      {hourly.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="t-heading" style={{ margin: 0 }}>The daily rhythm</h2>
            <span className="t-label subtle">the same hours, summed across every day in range</span>
          </div>
          <div className="card-body">
            {/* The grid above answers "which day AND hour"; this answers "which hour". It is a dial
                because hour-of-day has no start: 23:00 and 00:00 are adjacent, and the grid's axis
                cuts between them — which is exactly where an evening audience's peak sits. */}
            <RadialClock
              hours={hourly}
              label={HEATMAP_METRICS.find((m) => m.key === metric)?.label ?? 'Sessions'}
              valueLabel={HEATMAP_METRICS.find((m) => m.key === metric)?.label ?? 'Sessions'}
            />
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="t-heading" style={{ margin: 0 }}>Conversion funnel</h2>
          <span className="t-label subtle">each stage labelled with its source</span>
        </div>
        <div className="card-body">
          <Funnel stages={funnelStages} />
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          {/* "Explore breakdowns", not "Explore": the sidebar's Explore page (phase 6) is the
              cross-tenant query surface, and two things named Explore in one product would send
              people to the wrong one. This card breaks down THIS SITE's visitor analytics. */}
          <h2 className="t-heading" style={{ margin: 0 }}>Explore breakdowns</h2>
          <Select
            label="Break down by"
            value={dimension}
            // Page 1 on every change: page 4 of a pages list is past the end of a devices list,
            // which would render an empty table for no visible reason.
            onChange={(v) => { setDimension(v); setPage(1); }}
            options={[
              { value: Dimensions.pagePath, label: 'Pages' },
              { value: Dimensions.country, label: 'Countries' },
              { value: Dimensions.hostname, label: 'Domains' },
              { value: Dimensions.source, label: 'Sources' },
              { value: Dimensions.language, label: 'Languages' },
              { value: Dimensions.device, label: 'Devices' },
              { value: Dimensions.landingPage, label: 'Landing pages' },
              { value: Dimensions.browser, label: 'Browsers' },
              { value: Dimensions.operatingSystem, label: 'Operating systems' },
              { value: Dimensions.city, label: 'Cities' },
              { value: Dimensions.channelGroup, label: 'Channels' },
              { value: Dimensions.newVsReturning, label: 'New vs returning' },
            ]}
            inline
          />

          {/* Exports the table AS EXPLORED — same dimension, same filter, same range, and every
              row rather than just the page on screen. */}
          <ExportButton
            siteKey={siteKey}
            from={from}
            to={to}
            dimension={dimension}
            filterDimension={filter?.value ? filter.dimension : undefined}
            filterValue={filter?.value || undefined}
            label="Export this table"
          />
        </div>

        <div className={rows.isFetching ? 'is-refetching' : undefined}>
          <table className="table">
            <thead>
              <tr>
                <th>{dimension.replace(/_/g, ' ')}</th>
                <th className="num">Sessions</th>
                <th className="num">Users</th>
                <th className="num">Leads</th>
              </tr>
            </thead>
            <tbody>
              {(rows.data?.rows ?? []).map((r) => (
                <tr key={r.value}>
                  <td>{r.value || '(not set)'}</td>
                  <td className="num">{fmt(metricValue(r.metrics, 'sessions'))}</td>
                  <td className="num">{fmt(metricValue(r.metrics, 'users'))}</td>
                  <td className="num">{fmt(r.metrics[Metrics.leads] ?? null)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {rows.isError && <AnalyticsLoadError error={rows.error} what="the breakdown rows" />}
          {(rows.data?.rows.length ?? 0) === 0 && !rows.isLoading && !rows.isError && (
            <div className="empty-state t-body">No rows for this selection.</div>
          )}

          <Paginator
            page={rows.data?.page ?? page}
            perPage={rows.data?.per_page ?? perPage}
            total={rows.data?.total ?? 0}
            onPageChange={setPage}
            onPerPageChange={setPerPage}
            busy={rows.isFetching}
          />
        </div>
      </div>
    </>
  );
}

function fmt(value: number | null): string {
  return value === null ? '—' : formatCompact(value);
}
